/*
 * SS01888 — the hint menu of the formula / template editors popped open (top-left of the
 * page, unfiltered) whenever the caret was moved with an arrow key.
 *
 * The editors drive tributejs with trigger "#", requireLeadingSpace: false and
 * allowSpaces: true. On every keyup tribute re-reads the text preceding the caret
 * (TributeRange.getTriggerInfo) and, if it still finds a "#" there, calls showMenuFor():
 *
 *     if (current.trigger && commandEvent === false || isActive && keyCode === 8)
 *         showMenuFor(el, true)
 *
 * Only TAB/DELETE/ENTER/ESCAPE/SPACE/UP/DOWN set commandEvent, so the horizontal caret keys
 * fall straight through. That would be harmless if the trigger char never survived the
 * selection — but the seven system information units carry it in their own display name
 * ("#DocumentID", "#Date", "#State", ...), so the inserted <fts-i-unit> chip still reads
 * "#DocumentID" as text. Park the caret next to such a chip, press Right, and tribute sees
 * a fresh, still-open "#" mention: menu shown, positioned off the chip's own contenteditable
 * =false boundary rather than off a real caret.
 *
 * The "#" cannot be dropped from the chip — the backend resolves those very tokens
 * (FETIASEntity / VariableSchemaCollection read "#DocumentID" et al literally) and it is
 * what updateContent() writes back into field('...'). So the guard sits in front of tribute
 * instead: a capture-phase keyup listener on the component root swallows the caret-only keys
 * before they reach the editable element tribute is bound to, and closes the menu if one is
 * open. Capture on an ancestor is what makes the ordering deterministic — vue-tribute
 * re-attaches its own (target-phase) listeners whenever the options object changes, so a
 * listener registered alongside tribute's could not be relied on to run after it.
 *
 * Default actions are untouched: the caret still moves, which is all these keys should do.
 */

/* keys that only move the caret — none of them can start or extend a "#" mention */
const CARET_ONLY_KEYS = [
  "ArrowLeft",
  "ArrowRight",
  "Home",
  "End",
  "PageUp",
  "PageDown",
];

export default {
  methods: {
    /**
     * Stop caret movement from re-opening the tribute hint menu.
     * @param {string} refName ref of the contenteditable vue-tribute is attached to
     */
    bindTributeCaretGuard(refName = "formulaExpression") {
      if (this._tributeCaretGuard) return;

      this._tributeCaretGuard = (event) => {
        if (CARET_ONLY_KEYS.indexOf(event.key) < 0) return;

        const editor = this.$refs[refName];
        if (!editor || !editor.contains(event.target)) return;

        event.stopPropagation();

        /* vue-tribute hangs the live instance off the element it attached to */
        const tribute = editor.tributeInstance;
        if (tribute && tribute.isActive) tribute.hideMenu();
      };

      this.$el.addEventListener("keyup", this._tributeCaretGuard, true);
    },
    unbindTributeCaretGuard() {
      if (!this._tributeCaretGuard) return;

      this.$el.removeEventListener("keyup", this._tributeCaretGuard, true);
      this._tributeCaretGuard = null;
    },
  },
  beforeDestroy() {
    this.unbindTributeCaretGuard();
  },
};
